import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import PageViewTracker from "@/lib/PageViewTracker";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import SsoLanding from "@/components/SsoLanding";
import LandingPage from "@/components/LandingPage";
import LoginPage from "@/components/LoginPage";
import SignupPage from "@/components/SignupPage";
import ClientPortal from "@/components/ClientPortal";
import AdminDashboard from "@/components/AdminDashboard";
import PaymentSuccess from "@/components/PaymentSuccess";
import ForgotPassword from "@/components/ForgotPassword";
import ResetPassword from "@/components/ResetPassword";
import PricingPage from './components/PricingPage';
import Terms from "@/components/Terms";
import Privacy from "@/components/Privacy";
import NotFound from "@/components/NotFound";
import ServicePage from "@/components/ServicePage";
import RouteMeta from "@/seo/RouteMeta";
import seoData from "@/seo/pages.json";
import "@/App.css";

function portalCheckoutPath(searchParams) {
  const domain = searchParams.get("domain");
  if (!domain && searchParams.get("start") !== "checkout") return "/portal";
  const params = new URLSearchParams({ start: "checkout" });
  if (domain) params.set("domain", domain);
  const price = searchParams.get("price");
  const available = searchParams.get("available");
  if (price) params.set("price", price);
  if (available != null) params.set("available", available);
  return `/portal?${params.toString()}`;
}

function AuthGateRedirect({ adminPath = "/admin", fallback = "/portal" }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  if (user?.role === "admin") return <Navigate to={adminPath} replace />;
  const next = searchParams.get("domain") || searchParams.get("start") === "checkout"
    ? portalCheckoutPath(searchParams)
    : fallback;
  return <Navigate to={next} replace />;
}

function ProtectedRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/portal" />;
  return children;
}

// The editor's Sign out lands here so the portal session ends too.
function LogoutRoute() {
  // Every Sign out control is a plain link here, so leaving never depends on
  // a click handler. Runs the shared sign-out (server logout, clear storage,
  // editor clear, each with a timeout) and always ends on /login.
  const { signOutEverywhere } = useAuth();
  const ran = React.useRef(false);
  React.useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const fallback = setTimeout(() => window.location.replace('/login'), 7000);
    Promise.resolve(signOutEverywhere()).catch(() => window.location.replace('/login'))
      .finally(() => clearTimeout(fallback));
  }, [signOutEverywhere]);
  return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300" data-testid="logout-page">Signing you out…</div>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <Routes>
      <Route path="/" element={user ? <AuthGateRedirect /> : <LandingPage />} />
      <Route path="/login" element={user ? <AuthGateRedirect /> : <LoginPage />} />
      <Route path="/signup" element={user ? <AuthGateRedirect /> : <SignupPage />} />
      <Route path="/sso" element={<SsoLanding />} />
      <Route path="/logout" element={<LogoutRoute />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<PricingPage />} />
      {seoData.services.map((path) => (
        <Route key={path} path={path} element={<ServicePage path={path} />} />
      ))}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/portal/*" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><ClientPortal defaultTab="billing" /></ProtectedRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <RouteMeta />
      <PageViewTracker />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
